import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showSnackBar } from "../../components/Snackbar/Snackbar";
import { useForgotPasswordFlow } from "../../features/auth/useForgotPasswordFlow";
import { NEUTRAL_FORGOT_CSS } from "../../components/NeutralAuth/neutralCardCss";
import NeutralPortalHeader from "../../components/NeutralAuth/NeutralPortalHeader";

const ForgotPassword = ({ neutral = false }) => {
  const [email, setEmail] = useState('');
  const [reqError, setReqError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const flow = useForgotPasswordFlow()
  const isClient = !flow.isInternal
  const accent = flow.accent
  const accentHover = flow.accentHover

  useEffect(() => {
    if (neutral) return;
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, [neutral]);

  useEffect(() => {
    const handleReqError = () => {
      if (reqError && reqError.length > 0) {
        showSnackBar("error", reqError);
        setReqError('');
      }
    };

    handleReqError();
  }, [reqError]);

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleResetPassword = async () => {
    if (!isValidEmail(email)) {
      showSnackBar("error", "Invalid email address");
      return;
    }

    setLoading(true);
    try {
      const response = await dispatch(flow.requestCodeThunk({ email }))
      if (flow.requestCodeThunk.fulfilled.match(response)) {
        navigate(flow.confirmCodePath, { state: { email } })
      } else {
        const message = typeof response.payload === 'string' ? response.payload : 'Email is invalid'
        setReqError(message)
      }
    } catch (error) {
      console.error('Error in handleResetPassword:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{neutral ? NEUTRAL_FORGOT_CSS : `
        .fp-root {
          min-height: 100vh;
          background: #0f0f0f;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'DM Sans', sans-serif;
        }
        .fp-card {
          background: #161616;
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 16px;
          padding: 48px;
          width: 100%;
          max-width: 420px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
        }
        .fp-icon {
          width: 48px;
          height: 48px;
        }
        .fp-heading {
          color: #ffffff;
          font-size: 24px;
          font-weight: 600;
          margin: 0;
          text-align: center;
        }
        .fp-subtitle {
          color: rgba(255,255,255,0.45);
          font-size: 13px;
          line-height: 1.6;
          text-align: center;
          margin: 0;
        }
        .fp-form {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .fp-input {
          width: 100%;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          color: #ffffff;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          padding: 12px 16px;
          outline: none;
          box-sizing: border-box;
          transition: border-color 0.2s;
        }
        .fp-input::placeholder {
          color: rgba(255,255,255,0.3);
        }
        .fp-input:focus {
          border-color: ${accent}80;
        }
        .fp-btn {
          width: 100%;
          background: ${accent};
          color: #ffffff;
          border: none;
          border-radius: 10px;
          padding: 14px;
          font-size: 14px;
          font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: background 0.2s;
        }
        .fp-btn:hover:not(:disabled) {
          background: ${accentHover};
        }
        .fp-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .fp-link {
          color: rgba(255,255,255,0.45);
          font-size: 13px;
          text-decoration: none;
          transition: color 0.2s;
        }
        .fp-link:hover {
          color: rgba(255,255,255,0.7);
        }
      `}</style>
      <div className="fp-root">
        {neutral && <NeutralPortalHeader />}
        <div className="fp-card">
          <svg className="fp-icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="22" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
            <text x="24" y="30" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="22" fontFamily="DM Sans, sans-serif" fontWeight="600">?</text>
          </svg>
          <h2 className="fp-heading">Forgot your password?</h2>
          <p className="fp-subtitle">
            Please enter the email address associated with your account, and we'll email you a link to reset your password.
          </p>
          <div className="fp-form">
            <input
              className="fp-input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              className="fp-btn"
              onClick={handleResetPassword}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Reset Password'}
            </button>
          </div>
          <a className="fp-link" href={flow.signInPath}>Return to sign in</a>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
